import React from "react";
import { Plane } from "lucide-react";

// Mirrors backend AIRPORT_KEYWORDS (core/constants.py)
const AIRPORT_KEYWORDS = ["airport", "mke ", " mke", "mitchell international", "ord ", " ord", "o'hare", "ohare",
  "mdw ", " mdw", "midway", "msn ", " msn", "dane county regional", "atw ", " atw", "appleton international", "grb ", " grb", "green bay"];

export function isAirportAddress(addr) {
  const a = ` ${(addr || "").toLowerCase().trim()} `;
  return !!addr && AIRPORT_KEYWORDS.some(k => a.includes(k));
}

/** True when any of the route addresses looks like an airport. */
export function routeHasAirport(...addrs) {
  return addrs.flat().some(isAirportAddress);
}

export const emptyFlight = () => ({ airline: "", flight_number: "", direction: "arriving", scheduled_time: "", terminal: "" });
export const flightHasData = (f) => !!f && !!(f.airline?.trim() || f.flight_number?.trim() || f.scheduled_time?.trim() || f.terminal?.trim());
/** Payload for the API — null when nothing was entered so it never prints on the invoice. */
export const flightPayload = (f) => flightHasData(f) ? {
  airline: f.airline.trim() || null, flight_number: f.flight_number.trim() || null,
  direction: f.direction || null, scheduled_time: f.scheduled_time.trim() || null, terminal: f.terminal.trim() || null,
} : null;

const cls = "field-input";

/** Optional flight details block — render only when routeHasAirport(...) is true. */
export default function FlightInfoFields({ value, onChange, idPrefix }) {
  const f = value || emptyFlight();
  const set = (k) => (e) => onChange({ ...f, [k]: e.target.value });
  return (
    <div className="mt-3 border border-sky-500/30 bg-sky-500/5 p-3" data-testid={`${idPrefix}-flight-section`}>
      <div className="flex items-center gap-2 mb-2">
        <Plane size={13} className="text-sky-300"/>
        <span className="text-[10px] uppercase tracking-[0.22em] text-sky-300">Flight information</span>
        <span className="text-[10px] text-[#71717A]">optional — airport detected in the route</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <div>
          <label className="field-label">Airline</label>
          <input data-testid={`${idPrefix}-flight-airline`} className={cls} placeholder="Delta" value={f.airline} onChange={set("airline")}/>
        </div>
        <div>
          <label className="field-label">Flight #</label>
          <input data-testid={`${idPrefix}-flight-number`} className={cls} placeholder="DL1234" value={f.flight_number} onChange={set("flight_number")}/>
        </div>
        <div>
          <label className="field-label">Arriving / Departing</label>
          <select data-testid={`${idPrefix}-flight-direction`} className={cls} value={f.direction} onChange={set("direction")}>
            <option value="arriving">Arriving</option>
            <option value="departing">Departing</option>
          </select>
        </div>
        <div>
          <label className="field-label">Scheduled time</label>
          <input data-testid={`${idPrefix}-flight-time`} type="datetime-local" className={`${cls} [color-scheme:dark]`} value={f.scheduled_time} onChange={set("scheduled_time")}/>
        </div>
        <div>
          <label className="field-label">Terminal (optional)</label>
          <input data-testid={`${idPrefix}-flight-terminal`} className={cls} placeholder="C" value={f.terminal} onChange={set("terminal")}/>
        </div>
      </div>
    </div>
  );
}

/** Compact read-only line for invoice detail / pay page. */
export function FlightSummary({ flight, className = "" }) {
  if (!flight || !(flight.airline || flight.flight_number || flight.scheduled_time || flight.terminal)) return null;
  const when = flight.scheduled_time?.includes("T")
    ? new Date(flight.scheduled_time).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
    : flight.scheduled_time;
  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`} data-testid="flight-summary">
      <Plane size={12} className="text-sky-300"/>
      {[[flight.airline, flight.flight_number].filter(Boolean).join(" "),
        flight.direction ? flight.direction[0].toUpperCase() + flight.direction.slice(1) : null,
        when, flight.terminal ? `Terminal ${flight.terminal}` : null].filter(Boolean).join(" · ")}
    </span>
  );
}
